import { SDK } from '@commercetools/frontend-sdk';
import { getLocalizationInfo } from '@/project.config';
import { ComposableCommerceB2B, ComposableCommerceEventsB2B } from './composable-commerce-b2b';
import { CustomEvents } from './CustomEvents';
import { sessionExpiryRedirectUrl } from './sessionExpiryRedirect';

// Add other integration's custom events to the SDK's generic type here,
// by extending ComposableCommerceEvents with their type using an intersection.
// For example, <ComposableCommerceEvents & OtherEvents>.
// You may also wish to add your own custom events.
class CommercetoolsSDK extends SDK<ComposableCommerceEventsB2B & CustomEvents> {
  composableCommerce!: ComposableCommerceB2B;
  // Add any other integrations here.

  constructor() {
    super();
    this.composableCommerce = new ComposableCommerceB2B(this);
    // Initialize your other integrations here.

    this.on('errorCaught', (event) => {
      // Globally handle any errors caught by the SDK and integrations. For
      // example, log error, fire notification, etc.
      console.log('SDK error: ', event.data);
      // EWM-535 — a mid-session 401 from a non-auth action means the absolute
      // session timeout fired without a navigation (e.g. a background refetch
      // on an already-loaded page). Redirect to the login drawer with the
      // session-expired banner so the user is informed rather than silently broken.
      //
      // event.data shape (verified against BaseEvents.d.ts + SDK.js source):
      //   { type: 'action' | 'pageAPI', actionName?: string, error: ActionError, ... }
      // ActionError extends FetchError which copies all properties from the BFF
      // JSON response body — so error.statusCode is available when the BFF returns
      // { statusCode: 401, ... }.
      if (typeof window === 'undefined') return;
      const data = event.data;
      const actionName = data.type === 'action' ? data.actionName : undefined;
      const err = data.error as { statusCode?: number; errorName?: string };
      // The frontastic-session cookie is client-readable (the SDK fetcher reads
      // it to set the Frontastic-Session header), so we can tell an expired
      // authenticated session from a guest and pick the right login message.
      const sessionToken = window.document.cookie.match(/(?:^|;\s*)frontastic-session=([^;]+)/)?.[1];
      const url = sessionExpiryRedirectUrl(
        { statusCode: err?.statusCode, errorName: err?.errorName, actionName },
        window.location.pathname,
        window.location.search,
        sessionToken ? decodeURIComponent(sessionToken) : undefined,
      );
      if (url) window.location.assign(url);
    });

    this.on('fetchCalled', (event) => {
      console.log(`${event.data.type === 'action' ? event.data.actionName : event.data.method} called: `, event.data);
    });

    this.on('fetchSuccessful', (event) => {
      console.log(
        `${event.data.type === 'action' ? event.data.actionName : event.data.method}  successful: `,
        event.data,
      );
    });

    // Set up any other custom global event handlers here.
    // Ensure types are created and added to the SDK generic type
    // if specific to your project.
  }

  // A simplified, reusable method for configuring the SDK, as for
  // most cases only the locale and currency require input from runtime, or
  // may change on user input.
  defaultConfigure(localeString: string) {
    const { locale, currency } = getLocalizationInfo(localeString);

    this.configure({
      locale: locale.replace('-', '_'),
      currency,
      extensionVersion: process.env.NEXT_PUBLIC_EXT_BUILD_ID ?? 'dev',
      endpoint: process.env.NEXT_PUBLIC_FRONTASTIC_HOST as string,
    });
  }
}

// Create a single instance of the sdk.
const sdk = new CommercetoolsSDK();

// Export only the instance to serve as a singleton throughout
// the project.
export { sdk };
